import { useEffect, useState } from 'react';
import { Download, ScrollText } from 'lucide-react';
import { api } from './api.js';
import { Loading, Notice, displayDate } from './components.js';
import { t } from './i18n.js';

export function translateAction(s: string) {
  const labels: Record<string, string> = {
    'domain.created': t('Domaine ajouté'),
    'domain.verified': t('Vérification DNS'),
    'identity.user_invited': t('Collaborateur invité'),
    'identity.user_updated': t('Accès collaborateur modifié'),
    'identity.invitation_cancelled': t('Invitation annulée'),
    'security.login': t('Connexion sécurisée'),
    'security.mfa_enabled': t('Double authentification activée'),
    'security.recovery_used': t('Code de secours utilisé'),
    'security.logout': t('Déconnexion'),
    'security.session_revoked': t('Session révoquée'),
    'alias.created': t('Alias créé'),
    'alias.deleted': t('Alias supprimé'),
    'list.created': t('Liste créée'),
    'list.updated': t('Liste modifiée'),
    'list.deleted': t('Liste supprimée'),
    'mail.submission_queued': t('Envoi pris en charge'),
    'mail.submission_processed': t('Envoi traité'),
    'mail.submission_scheduled': t('Envoi programmé'),
    'mail.submission_released': t('Envoi programmé libéré'),
    'mail.submission_canceled': t('Envoi programmé annulé'),
    'mail.submission_scheduled_failed': t('Envoi programmé en échec'),
    'mail.rule.create': t('Règle de tri créée'),
    'mail.rule.update': t('Règle de tri modifiée'),
    'mail.rule.delete': t('Règle de tri supprimée'),
    'mail.rule.reorder': t('Règles de tri réordonnées'),
    'mail.autoreply.update': t('Réponse d’absence modifiée'),
    'mail.autoreply.sent': t('Réponse d’absence envoyée'),
    'mail.export': t('Message exporté'),
    'mail.purged': t('Messages supprimés définitivement'),
    'contact.create': t('Contact ajouté'),
    'contact.update': t('Contact modifié'),
    'contact.delete': t('Contact supprimé'),
    'contact.import': t('Carnet importé'),
    'organization.exported': t('Configuration exportée'),
    'profile.updated': t('Profil modifié'),
    'audit.exported': t('Journal d’activité exporté'),
    'team_template.created': t('Modèle d’équipe créé'),
    'team_template.updated': t('Modèle d’équipe modifié'),
    'team_template.deleted': t('Modèle d’équipe supprimé'),
    'company_signature.updated': t('Signature d’entreprise modifiée'),
  };
  return labels[s] ?? s;
}
const categories = (): [string, string][] => [
  ['', t('Toutes les actions')],
  ['security.', t('Sécurité et connexions')],
  ['identity.', t('Collaborateurs')],
  ['domain.', t('Domaines')],
  ['alias.', t('Alias')],
  ['list.', t('Listes')],
  ['mail.submission', t('Envois')],
  ['mail.rule', t('Règles de tri')],
  ['mail.autoreply', t('Réponse d’absence')],
  ['contact.', t('Carnet d’adresses')],
  ['organization.', t('Organisation')],
  ['team_template.', t('Modèles d’équipe')],
  ['company_signature.', t('Signature d’entreprise')],
  ['audit.', t('Journal')],
];
interface AuditPage {
  items: any[];
  nextCursor: number | null;
  total: number;
  windowDays: number;
  floor: string;
}
const dayInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function AuditLog() {
  const today = new Date();
  const [period, setPeriod] = useState<'7' | '30' | '180' | 'custom'>('30'),
    [from, setFrom] = useState(dayInput(new Date(today.getTime() - 6 * 86400_000))),
    [to, setTo] = useState(dayInput(today)),
    [action, setAction] = useState(''),
    [q, setQ] = useState(''),
    [debounced, setDebounced] = useState(''),
    [page, setPage] = useState<AuditPage>(),
    [loading, setLoading] = useState(true),
    [more, setMore] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);
  useEffect(() => {
    if (page?.windowDays === 30 && period === '180') setPeriod('30');
  }, [page?.windowDays, period]);
  const query = (cursor?: number | null) => {
    const params = new URLSearchParams();
    if (period !== 'custom')
      params.set('from', new Date(Date.now() - Number(period) * 86400_000).toISOString());
    if (period === 'custom') {
      params.set('from', new Date(from + 'T00:00:00').toISOString());
      params.set('to', new Date(to + 'T23:59:59').toISOString());
    }
    if (action) params.set('action', action);
    if (debounced) params.set('q', debounced);
    if (cursor) params.set('cursor', String(cursor));
    return params.toString();
  };
  useEffect(() => {
    let disposed = false;
    setLoading(true);
    api<AuditPage>('/audit-events?' + query())
      .then((r) => {
        if (disposed) return;
        setPage(r);
        setError('');
      })
      .catch((e) => !disposed && setError(e.message))
      .finally(() => !disposed && setLoading(false));
    return () => {
      disposed = true;
    };
  }, [period, from, to, action, debounced]);
  const loadMore = async () => {
    if (!page?.nextCursor) return;
    setMore(true);
    try {
      const r = await api<AuditPage>('/audit-events?' + query(page.nextCursor));
      setPage({ ...r, items: [...page.items, ...r.items] });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setMore(false);
    }
  };
  return (
    <div className="audit-log">
      <div className="audit-filters">
        <label>
          {t('Période')}
          <select value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>
            <option value="7">{t('7 derniers jours')}</option>
            <option value="30">{t('30 derniers jours')}</option>
            {page?.windowDays === 180 && <option value="180">{t('180 derniers jours')}</option>}
            <option value="custom">{t('Dates précises')}</option>
          </select>
        </label>
        {period === 'custom' && (
          <>
            <label>
              {t('Du')}
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              {t('Au')}
              <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </label>
          </>
        )}
        <label>
          {t('Type d’action')}
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            {categories().map(([value, label]) => (
              <option key={value || 'all'} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="audit-search">
          {t('Recherche')}
          <input
            type="search"
            value={q}
            maxLength={120}
            placeholder={t('Acteur, cible ou détail')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>
      <div className="audit-summary">
        <span>
          {page
            ? t(page.total > 1 ? '{n} événements sur la période' : '{n} événement sur la période', {
                n: page.total,
              })
            : ''}
          {page && (
            <small>
              {' '}
              · {t('Consultation limitée aux {n} derniers jours', { n: page.windowDays })}
            </small>
          )}
        </span>
        <a className="text-button" href={'/v1/audit-events/export?' + query()} download>
          <Download size={16} /> {t('Exporter (CSV)')}
        </a>
      </div>
      {error && <Notice type="error">{error}</Notice>}
      {loading ? (
        <Loading label={t('Chargement du journal…')} />
      ) : !page || page.items.length === 0 ? (
        <div className="empty-state">
          <ScrollText />
          <h3>{t('Aucun événement sur cette période')}</h3>
          <p>{t('Élargissez la période ou retirez un filtre.')}</p>
        </div>
      ) : (
        <>
          <div className="audit-list">
            {page.items.map((e) => (
              <div className="audit-row" key={e.id}>
                <span className="audit-dot" />
                <div>
                  <strong>{translateAction(e.action)}</strong>
                  <small>
                    {e.actor}
                    {e.actorEmail ? ` (${e.actorEmail})` : ''}
                    {e.target && e.target !== e.actorEmail ? ` · ${e.target}` : ''}
                    {e.detail && ` · ${e.detail}`}
                  </small>
                </div>
                <time>{displayDate(e.createdAt, true)}</time>
              </div>
            ))}
          </div>
          {page.nextCursor && (
            <button
              className="secondary small audit-more"
              onClick={() => void loadMore()}
              disabled={more}
            >
              {more ? t('Chargement…') : t('Charger la suite')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
